import { useState } from 'react'
import { Pencil, Trash2 } from 'lucide-react'

import { NoteMarkdown } from '@/components/NoteMarkdown'
import { RichMarkdownEditor } from '@/components/RichMarkdownEditor'
import { initials } from '@/lib/initials'
import {
  formatRelativeDateTime,
  formatShortRelativeDateTime,
} from '@/lib/relativeDateTime'

export type CalloutNoteMode = 'read' | 'edit' | 'new'

export interface CalloutNoteProps {
  authorName: string
  createdAt?: string
  text?: string
  mode?: CalloutNoteMode
  /**
   * Seed for the edit-mode editor when it should differ from `text` — set
   * when a read-mode checkbox tap opened the editor with that box already
   * flipped, so Save is immediately active with exactly that change.
   */
  initialDraftText?: string
  /**
   * Makes read-mode checkboxes tappable (see NoteMarkdown.onToggleTask).
   * Absent = checkboxes render inert.
   */
  onToggleTask?: (taskIndex: number) => void
  /** Small transient message under the note (e.g. a permissions denial). */
  notice?: string | null
  /** When false in read mode, hides the edit button. */
  showEditButton?: boolean
  /** When true in read mode, shows a delete button. */
  showDeleteButton?: boolean
  /** Optional callback when the edit icon is clicked in read mode. */
  onClickEdit?: () => void
  /** Optional callback when delete is clicked in read mode. */
  onClickDelete?: () => void
  /** Optional callback when cancel is clicked in edit mode. */
  onClickCancel?: () => void
  /**
   * Optional callback when save is clicked in edit mode.
   * Receives the latest textarea value.
   */
  onClickSave?: (nextText: string) => void
  /**
   * Optional callback when "Add note" is clicked in new mode.
   * Receives the textarea value.
   */
  onClickAddNote?: (nextText: string) => boolean
  /** Optional callback when "Upload photo" is clicked in new mode. */
  onClickAddPhoto?: () => void
}

/** The yellow note card look shared with the Services side panel. */
export const noteCardClass =
  'flex gap-2 rounded-lg border border-[#e7e2d5] bg-[#fffdf6] p-2.5'

export function NoteAvatar({ name }: { name: string }) {
  return (
    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-200 text-[10px] font-bold text-amber-800">
      {initials(name)}
    </div>
  )
}

export function NoteTimestamp({ createdAt }: { createdAt?: string }) {
  if (!createdAt) return null
  return (
    <span
      className="font-mono text-[10.5px] whitespace-nowrap text-slate-400"
      title={formatRelativeDateTime(createdAt)}
    >
      {formatShortRelativeDateTime(createdAt)}
    </span>
  )
}

export function CalloutNote(props: CalloutNoteProps) {
  const {
    authorName,
    createdAt,
    text = '',
    mode = 'read',
    initialDraftText,
    onToggleTask,
    notice,
    showEditButton = true,
    showDeleteButton = false,
    onClickEdit,
    onClickDelete,
    onClickCancel,
    onClickSave,
    onClickAddNote,
    onClickAddPhoto,
  } = props

  if (mode === 'edit') {
    return (
      <NoteEditor
        authorName={authorName}
        createdAt={createdAt}
        savedText={text}
        initialDraftText={initialDraftText}
        onClickCancel={onClickCancel}
        onClickSave={onClickSave}
      />
    )
  }

  if (mode === 'new') {
    return (
      <NewNoteComposer
        authorName={authorName}
        onClickAddNote={onClickAddNote}
        onClickAddPhoto={onClickAddPhoto}
      />
    )
  }

  return (
    <div className={noteCardClass}>
      <NoteAvatar name={authorName} />
      <div className="min-w-0 flex-1">
        <div className="mb-0.5 flex items-center gap-1.5">
          <span className="text-xs font-semibold text-slate-900">
            {authorName}
          </span>
          <span className="text-[10.5px] text-amber-700">wrote a note</span>
          <span className="flex-1" />
          <NoteTimestamp createdAt={createdAt} />
          {showEditButton ? (
            <button
              type="button"
              className="inline-flex h-6 w-6 items-center justify-center rounded-full text-slate-400 hover:bg-amber-100 hover:text-slate-600"
              onClick={onClickEdit}
              aria-label="Edit note"
            >
              <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          ) : null}
          {showDeleteButton ? (
            <button
              type="button"
              className="inline-flex h-6 w-6 items-center justify-center rounded-full text-slate-400 hover:bg-red-50 hover:text-red-600"
              onClick={onClickDelete}
              aria-label="Delete note"
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          ) : null}
        </div>
        <NoteMarkdown
          text={text}
          className="text-[12.5px] leading-normal text-stone-900"
          onToggleTask={onToggleTask}
        />
        {notice ? <NoteNotice message={notice} /> : null}
      </div>
    </div>
  )
}

/** Transient inline message — a checkbox tap that the viewer may not make. */
export function NoteNotice({ message }: { message: string }) {
  return (
    <output className="mt-1 block text-[11px] text-amber-700">{message}</output>
  )
}

/**
 * Edit mode. Save stays disabled until the draft actually differs from what
 * is stored — the techs' complaint was a save button that looked live when
 * there was nothing to save, which read as "my change went through".
 */
function NoteEditor(props: {
  authorName: string
  createdAt?: string
  savedText: string
  initialDraftText?: string
  onClickCancel?: () => void
  onClickSave?: (nextText: string) => void
}) {
  const { authorName, createdAt, savedText, initialDraftText } = props
  const seed = initialDraftText ?? savedText
  const [draftText, setDraftText] = useState(seed)
  const isDirty = draftText !== savedText

  return (
    <div className={noteCardClass}>
      <NoteAvatar name={authorName} />
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-semibold text-slate-900">
            {authorName}
          </span>
          <span className="text-[10.5px] tracking-wide text-amber-700 uppercase">
            Editing
          </span>
          <span className="flex-1" />
          <NoteTimestamp createdAt={createdAt} />
        </div>
        <RichMarkdownEditor
          initialValue={seed}
          onChange={setDraftText}
          autoFocus
          ariaLabel="Note text"
          className="bg-white/70"
        />
        <div className="flex items-center gap-2 pt-1">
          <button
            type="button"
            className="inline-flex flex-1 items-center justify-center rounded-md border border-slate-300 bg-white px-3 py-2 text-xs text-slate-700 hover:bg-slate-50"
            onClick={props.onClickCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!isDirty}
            className={`inline-flex flex-1 items-center justify-center rounded-md px-3 py-2 text-xs font-medium ${
              isDirty
                ? 'bg-slate-900 text-white hover:bg-slate-800'
                : 'cursor-not-allowed bg-slate-200 text-slate-400'
            }`}
            onClick={() => {
              if (!props.onClickSave || !isDirty) return
              props.onClickSave(draftText)
            }}
          >
            {isDirty ? 'Save note' : 'Saved'}
          </button>
        </div>
      </div>
    </div>
  )
}

function NewNoteComposer(props: {
  authorName: string
  onClickAddNote?: (nextText: string) => boolean
  onClickAddPhoto?: () => void
}) {
  const { authorName, onClickAddNote, onClickAddPhoto } = props
  const [draftText, setDraftText] = useState('')
  // Remounts the editor to clear it once a note is added — tiptap owns its
  // document, so resetting the draft string alone would leave the old text.
  const [editorGeneration, setEditorGeneration] = useState(0)

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span className="font-medium text-slate-700">{authorName}</span>
        <span className="text-[11px] tracking-wide text-slate-400 uppercase">
          New note
        </span>
      </div>
      <RichMarkdownEditor
        key={editorGeneration}
        initialValue=""
        onChange={setDraftText}
        placeholder="Type a quick note from site…"
        ariaLabel="New note text"
      />
      <div className="mt-2 flex gap-2">
        <button
          type="button"
          className="inline-flex flex-1 items-center justify-center rounded-md border border-slate-300 bg-white px-3 py-2 text-xs text-slate-700 hover:bg-slate-50"
          onClick={() => {
            if (!onClickAddNote) return
            const didAdd = onClickAddNote(draftText)
            if (didAdd) {
              setDraftText('')
              setEditorGeneration((generation) => generation + 1)
            }
          }}
        >
          Add note
        </button>
        <button
          type="button"
          className="inline-flex flex-1 items-center justify-center rounded-md border border-slate-300 bg-white px-3 py-2 text-xs text-slate-700 hover:bg-slate-50"
          onClick={onClickAddPhoto}
        >
          Upload photo
        </button>
      </div>
    </div>
  )
}
